import { useMemo, useState } from 'react';

import { buildBaseQuery } from './list-screen-utils';

import type { DrawerMode, ListScreenProps } from './types';
import type { DataGridState } from '@/components/data-grid';
import type { UseQueryResult } from '@tanstack/react-query';
import type { PaginatedResponse } from '@utazzo/shared';

import { useDataGridState, type UseDataGridStateResult } from '@/hooks/use-data-grid-state';

export interface UseListScreenResult<TRow, TQuery> {
  state: DataGridState;
  gridState: UseDataGridStateResult;
  query: TQuery;
  list: UseQueryResult<PaginatedResponse<TRow>>;
  data: ReadonlyArray<TRow>;
  pagination: { page: number; pageSize: number; totalItems: number } | null;
  drawer: {
    mode: DrawerMode | null;
    row: TRow | null;
    openCreate: () => void;
    openEdit: (row: TRow) => void;
    close: () => void;
  };
  deletion: {
    target: TRow | null;
    targets: TRow[] | null;
    request: (row: TRow) => void;
    requestBulk: (rows: TRow[]) => void;
    clear: () => void;
    clearBulk: () => void;
  };
}

/**
 * Owns every mechanical + stateful concern of a list screen: URL-synced grid
 * state, query composition, the data request, and the create/edit-drawer +
 * delete interaction state. `<ListScreen>` consumes this; a page with exotic
 * needs can call it directly and render its own JSX.
 */
export function useListScreen<TRow, TQuery>(
  config: ListScreenProps<TRow, TQuery>,
): UseListScreenResult<TRow, TQuery> {
  const filterIds = useMemo(() => (config.filters ?? []).map((f) => f.id), [config.filters]);

  // Seed hidden columns from each column's `defaultHidden` flag. Only resolvable
  // when `columns` is a static array (the common case); state-aware column
  // functions opt out (they can't be evaluated before the grid state exists).
  const defaultHidden = useMemo(
    () =>
      Array.isArray(config.columns)
        ? config.columns.filter((c) => c.defaultHidden).map((c) => c.id)
        : [],
    [config.columns],
  );

  const gridState = useDataGridState({
    defaultPageSize: config.defaultPageSize,
    defaultSortBy: config.defaultSort.by,
    defaultSortDir: config.defaultSort.dir,
    filterIds,
    defaultHidden,
    defaultFilters: config.defaultFilters,
  });
  const { state } = gridState;

  const query = config.toQuery(
    state,
    buildBaseQuery(state, {
      defaultSortBy: config.defaultSort.by,
      minSearchLength: config.minSearchLength,
    }),
  );

  const list = config.useList(query);

  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
  const [drawerRow, setDrawerRow] = useState<TRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TRow | null>(null);
  const [bulkTargets, setBulkTargets] = useState<TRow[] | null>(null);

  const data = list.data?.data ?? [];
  const pagination = list.data
    ? {
        page: list.data.pagination.page,
        pageSize: list.data.pagination.pageSize,
        totalItems: list.data.pagination.totalItems,
      }
    : null;

  return {
    state,
    gridState,
    query,
    list,
    data,
    pagination,
    drawer: {
      mode: drawerMode,
      row: drawerRow,
      openCreate: () => {
        setDrawerRow(null);
        setDrawerMode('create');
      },
      openEdit: (row) => {
        setDrawerRow(row);
        setDrawerMode('edit');
      },
      close: () => {
        setDrawerMode(null);
        setDrawerRow(null);
      },
    },
    deletion: {
      target: deleteTarget,
      targets: bulkTargets,
      request: (row) => setDeleteTarget(row),
      requestBulk: (rows) => setBulkTargets(rows),
      clear: () => setDeleteTarget(null),
      clearBulk: () => setBulkTargets(null),
    },
  };
}
